/**
 * Puck Configuration for Web Player
 * Defines render functions for all components created in Admin.
 * optimized for the player (rendering only).
 */

import React from 'react';

// --- Helper Styles ---
const getButtonStyles = (variant, size, borderRadius, backgroundColor, textColor) => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.2s ease-in-out',
    textDecoration: 'none',
    border: 'none',
    fontFamily: 'Montserrat, sans-serif',
  };
  // Defaults
  const v = variant || 'solid';
  const s = size || 'medium';
  const r = borderRadius || 'medium';
  const bg = backgroundColor || '#dd3333';
  const color = textColor || '#ffffff';

  const sizes = {
    small: { padding: '6px 12px', fontSize: '14px' },
    medium: { padding: '10px 20px', fontSize: '16px' },
    large: { padding: '14px 28px', fontSize: '18px' },
  };
  const radii = { none: '0px', small: '4px', medium: '8px', large: '16px', full: '9999px' };

  const style = { ...base, ...(sizes[s] || sizes.medium), borderRadius: radii[r] || radii.medium };
  
  if (v === 'outline') {
    return { ...style, backgroundColor: 'transparent', color: bg, border: `2px solid ${bg}` };
  } else if (v === 'ghost') {
    return { ...style, backgroundColor: 'transparent', color: bg };
  }
  // Solid
  return { ...style, backgroundColor: bg, color: color, border: `2px solid ${bg}` };
};

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M3 2l10 6-10 6V2z" /></svg>
);
const SpeakerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
  </svg>
);

// --- Components ---

const Container = {
  // Note: If Admin uses DropZone with "container-content", we can't easily render it here 
  // unless we know how to access that zone. Puck usually passes slots as props if defined in fields.
  render: ({ backgroundColor, textColor, flexDirection, alignItems, justifyContent, padding, margin, borderRadius, shadow, minHeight, gap, children, content }) => (
    <div style={{
      display: 'flex',
      flexDirection: flexDirection || 'column',
      alignItems: alignItems || 'flex-start',
      justifyContent: justifyContent || 'flex-start',
      backgroundColor: backgroundColor || 'transparent', 
      color: textColor,
      padding,
      margin,
      borderRadius,
      boxShadow: shadow === 'medium' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
      minHeight: minHeight || 'auto',
      gap: gap || '0px',
      width: '100%'
    }}>
      {content || children}
    </div>
  )
};

const Flex = {
  fields: {
    content: { type: 'slot' }
  },
  // Puck passes the slot as a component function (Content), so we must render it as <Content />
  render: ({ direction, justifyContent, alignItems, gap, padding, backgroundColor, children, content: Content }) => (
    <div style={{ backgroundColor: backgroundColor || 'transparent', padding, width: '100%' }}>
      <div style={{
        display: 'flex',
        flexDirection: direction || 'row',
        justifyContent: justifyContent || 'flex-start',
        alignItems: alignItems || 'flex-start',
        gap: gap || '16px',
        width: '100%'
      }}>
        {Content ? <Content /> : children}
      </div>
    </div>
  )
};

const ResponsiveGrid = {
  fields: {
    content: { type: 'slot' }
  },
  // Puck passes the slot as a component function (Content), so we must render it as <Content />
  render: ({ minCardWidth, gap, padding, backgroundColor, children, content: Content, ...rest }) => {
    console.log('[ResponsiveGrid] Rendering with:', { 
      hasContent: !!Content, 
      contentType: typeof Content,
      hasChildren: !!children,
      restProps: Object.keys(rest)
    });
    
    // Try rendering Content with style prop (Puck might need this)
    const contentElement = Content ? <Content style={{
      display: 'contents' // Use 'contents' to make grid items direct children
    }} /> : children;
    
    return (
      <div style={{ backgroundColor: backgroundColor || 'transparent', padding, width: '100%' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${minCardWidth || '280px'}, 1fr))`,
          gap: gap || '16px',
          width: '100%'
        }}>
           {contentElement}
        </div>
      </div>
    );
  }
};

const Text = {
  render: ({ text, fontSize, fontWeight, textAlign, color, margin }) => (
    <div style={{
      fontSize: fontSize || '16px',
      fontWeight: fontWeight || '400',
      textAlign: textAlign || 'left',
      color: color || 'inherit',
      margin: margin || '0 0 16px 0',
      whiteSpace: 'pre-wrap'
    }}>
      {text}
    </div>
  )
};

const Button = {
  render: ({ text, href, variant, size, borderRadius, backgroundColor, textColor }) => {
    const style = getButtonStyles(variant, size, borderRadius, backgroundColor, textColor);
    if (href) {
      const handleClick = (e) => {
        // External URLs - let browser handle
        if (href.startsWith('http://') || href.startsWith('https://')) return;
        // Internal URLs - use SPA navigation
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('navigateTo', { detail: { url: href } }));
      };
      return <a href={href} onClick={handleClick} style={style}>{text}</a>;
    }
    return <button style={style}>{text}</button>;
  }
};

const HeroSection = {
  render: ({ title, subtitle, backgroundImage, styling, textAlign, buttonText, buttonLink, overlayImage, overlaySettings, overlayPosition, overlaySize }) => {
    // Support both old and new data structures
    const backgroundColor = styling?.backgroundColor || '#1a1a1a';
    const textColor = styling?.textColor || '#ffffff';
    const height = styling?.height || '500px';

    // Get overlay settings - support both new overlaySettings and legacy props
    const getOverlayConfig = () => {
      // If new overlaySettings exists, use it
      if (overlaySettings) {
        return {
          positionX: overlaySettings.positionX ?? 5,
          positionY: overlaySettings.positionY ?? 5,
          size: overlaySettings.size ?? 120,
        };
      }
      // Fall back to legacy position/size props
      const legacySizeMap = { small: 80, medium: 120, large: 180 };
      const legacyPositionMap = {
        'top-left': { x: 5, y: 5 },
        'top-center': { x: 50, y: 5 },
        'top-right': { x: 95, y: 5 },
        'center': { x: 50, y: 50 },
        'bottom-left': { x: 5, y: 95 },
        'bottom-center': { x: 50, y: 95 },
        'bottom-right': { x: 95, y: 95 },
      };
      const pos = legacyPositionMap[overlayPosition] || { x: 5, y: 5 };
      return {
        positionX: pos.x,
        positionY: pos.y,
        size: legacySizeMap[overlaySize] || 120,
      };
    };

    const overlayConfig = getOverlayConfig();

    // Calculate overlay position styles using percentages
    const getOverlayStyles = () => ({
      position: 'absolute',
      zIndex: 10,
      left: `${overlayConfig.positionX}%`,
      top: `${overlayConfig.positionY}%`,
      maxWidth: `${overlayConfig.size}px`,
      maxHeight: `${overlayConfig.size}px`,
      objectFit: 'contain',
      // Adjust transform based on position to prevent overflow
      transform: `translate(${overlayConfig.positionX > 50 ? '-100%' : '0'}, ${overlayConfig.positionY > 50 ? '-100%' : '0'})`,
    });

    const handleButtonClick = (e) => {
      if (!buttonLink || buttonLink === '#') return;
      // External URLs - let browser handle
      if (buttonLink.startsWith('http://') || buttonLink.startsWith('https://')) return;
      // Internal URLs - use SPA navigation
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { url: buttonLink } }));
    };

    return (
      <div style={{
        height,
        backgroundColor,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px',
        color: textColor,
        textAlign: textAlign || 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {backgroundImage && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)' }} />}
        {overlayImage && <img src={overlayImage} alt="Overlay" style={getOverlayStyles()} />}
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px' }}>
          <h1 style={{ fontSize: '48px', fontWeight: '700', margin: '0 0 16px 0' }}>{title}</h1>
          <p style={{ fontSize: '20px', margin: '0 0 32px 0', opacity: 0.9 }}>{subtitle}</p>
          {buttonText && <a href={buttonLink || '#'} onClick={handleButtonClick} style={getButtonStyles('solid', 'medium', 'medium', '#3b82f6', '#fff')}>{buttonText}</a>}
        </div>
      </div>
    );
  }
};

const DjMixesCard = {
  render: ({ imageUrl, category, title, handle, actionType, navigateUrl, audioFileUrl, buttonText, styling }) => {
    // Support both old and new data structures
    const buttonColor = styling?.buttonColor || '#dd3333';
    const backgroundColor = styling?.backgroundColor || '#0a0a0a';
    const textColor = styling?.textColor || '#ffffff';
    const imageAspectRatio = styling?.imageAspectRatio || '1/1';

    // Determine effective action type: explicit actionType, or infer from available URLs
    const effectiveActionType = actionType === 'playAudio' ? 'playAudio'
      : (actionType === 'navigate' && navigateUrl) ? 'navigate'
      : audioFileUrl ? 'playAudio'
      : 'navigate';

    // Determine button text based on action type or custom buttonText
    const displayButtonText = buttonText || (effectiveActionType === 'navigate' ? 'VIEW' : 'LISTEN');

    const handleButtonClick = () => {
      if (actionType === 'navigate' && navigateUrl) {
        // Navigate to page - supports both internal slugs (/dj-mix-1) and external URLs (https://...)
        if (navigateUrl.startsWith('http')) {
          // External URL - open in new tab
          window.open(navigateUrl, '_blank');
        } else {
          // Internal slug - use SPA navigation (preserves audio playback)
          console.log('[DjMixesCard] SPA navigation to:', navigateUrl);
          window.dispatchEvent(new CustomEvent('navigateTo', { detail: { url: navigateUrl } }));
        }
      } else if (audioFileUrl) {
        // Play audio if we have an audio file
        console.log('[DjMixesCard] Dispatching playAudio:', title);
        window.dispatchEvent(new CustomEvent('playAudio', { 
          detail: { 
            url: audioFileUrl, 
            title: title,
            artist: category || 'Shaq Fu Radio',
            cover: imageUrl || ''
          } 
        }));
      }
    };
    
    // Check if this card's audio is currently playing
    const isPlaying = window.__CURRENT_PLAYING_URL__ === audioFileUrl;
    const color = buttonColor;
    
    return (
      <div style={{
        backgroundColor,
        color: textColor,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        fontFamily: 'Montserrat, sans-serif',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.4)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)';
      }}
      >
        <div style={{ position: 'relative', width: '100%', aspectRatio: imageAspectRatio, overflow: 'hidden' }}>
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={title} 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                transition: 'transform 0.3s ease-in-out',
                transform: 'scale(1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', backgroundColor: '#2a2a2a' }} />
          )}
        </div>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
          {category && <span style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: textColor, opacity: 0.9 }}>{category}</span>}
          <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', lineHeight: 1.2 }}>{title}</h3>
          {handle && <span style={{ fontSize: '14px', opacity: 0.6 }}>{handle}</span>}
          <button 
            onClick={handleButtonClick} 
            style={{
              marginTop: 'auto',
              padding: '12px 32px',
              fontSize: '13px',
              fontWeight: '600',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              borderRadius: '25px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: isPlaying ? color : 'transparent',
              color: isPlaying ? '#ffffff' : color,
              border: `2px solid ${color}`,
              alignSelf: 'flex-start',
              transition: 'all 0.3s ease-in-out'
            }}
            onMouseEnter={(e) => {
              if (!isPlaying) {
                e.currentTarget.style.background = color;
                e.currentTarget.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!isPlaying) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = color;
              }
            }}
          >
            {effectiveActionType === 'navigate' ? <ArrowRightIcon /> : <SpeakerIcon />} {displayButtonText}
          </button>
        </div>
      </div>
    );
  }
};

const CollectionCard = {
  render: ({ collectionId, title, description, imageUrl, showImage, showDescription, showButton, buttonText, buttonColor, backgroundColor, textColor, borderRadius }) => {
    const handleButtonClick = () => {
      if (collectionId) {
        console.log('[CollectionCard] Playing collection:', collectionId, title);
        // Dispatch event to play collection (you can extend this to fetch collection tracks)
        window.dispatchEvent(new CustomEvent('playCollection', { detail: { collectionId, title } }));
      }
    };
    
    return (
      <div style={{
        backgroundColor: backgroundColor || '#1a1a1a',
        color: textColor || '#fff',
        borderRadius: borderRadius || '12px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        fontFamily: 'Montserrat, sans-serif'
      }}>
        {showImage && imageUrl && <img src={imageUrl} alt={title} style={{ width: '100%', aspectRatio: '3/2', objectFit: 'cover' }} />}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>{title}</h3>
          {showDescription && <p style={{ margin: 0, fontSize: '14px', opacity: 0.85, flex: 1 }}>{description}</p>}
          {showButton && (
            <button 
              onClick={handleButtonClick}
              style={getButtonStyles('solid', 'medium', 'medium', buttonColor || '#dd3333', '#fff')}
            >
              <PlayIcon /> {buttonText || 'Listen Now'}
            </button>
          )}
        </div>
      </div>
    );
  }
};

// --- Export ---
export const puckConfig = {
  components: { Container, Flex, ResponsiveGrid, Grid: ResponsiveGrid, Text, Button, HeroSection, DjMixesCard, CollectionCard }
};
export default puckConfig;
